import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import Position from './Position';
import { useState, useEffect, useRef } from "react";
import ConfirmModal from "../elements/modals/ConfirmModal";
import Helpers from "@/Helpers";

export default function BehavioralInventory(props) {
   const { sectionData, values, setValue } = props;
   const [showValidationModal, setShowValidationModal] = useState(false);
   const hasShownBlankPromptRef = useRef(false);

   if (!sectionData)
      return null;

   const currentSection = sectionData?.currentSection;
   const page = Number(sectionData?.page || 1);
   const content: Record<string, string> = Helpers.getSectionContent(currentSection, sectionData?.lang);
   const elements = Helpers.getSectionElements(content);
   const contentItems = Array.isArray(elements?.items) ? elements.items : [];
   const instructionsHtml = Helpers.getSectionHtmlValue(content, "instructions");
   const currentPageItems = contentItems.filter((item) => {
      if (item?.page === undefined || item?.page === null) return true;
      return Number(item.page) === page;
   });

   function hasAnyAnswerOnPage(validationValues) {
      return currentPageItems.some((item) => {
         const currentValue = validationValues?.[currentSection.formId + item.id];
         return !(currentValue === undefined || currentValue === null || currentValue === "" || currentValue === 0 || currentValue === "0");
      });
   }

   useEffect(() => {
      sectionData?.registerSection( 767, contentItems.length);
   }, [sectionData?.currentSection?.id]);

   useEffect(() => {
      // Reset one-time blank-page prompt state for each new section/page.
      hasShownBlankPromptRef.current = false;
      setShowValidationModal(false);
   }, [currentSection?.id, sectionData?.page]);

   useEffect(() => {
      const nextCount = Number(sectionData?.nextCount || 0);
      const hasAnyAnswer = hasAnyAnswerOnPage(values);
      const issueCount = !hasAnyAnswer && nextCount === 1 ? 1 : 0;
      const shouldPromptForBlankPage = issueCount > 0;

      sectionData?.updateIssueCount?.(issueCount);

      if (shouldPromptForBlankPage && !hasShownBlankPromptRef.current) {
         hasShownBlankPromptRef.current = true;
         setShowValidationModal(true);
         return;
      }

      if (!shouldPromptForBlankPage) {
         setShowValidationModal(false);
      }

   }, [sectionData?.nextCount, sectionData?.updateIssueCount, values, currentSection?.formId, sectionData?.page, currentPageItems]);

   useEffect(() => {
      if (!sectionData?.isAdmin) return;

      const handleKeyDown = (e) => {
         if (typeof sectionData?.onKeyDown === "function") {
            sectionData.onKeyDown(e);
         }

         if (!["1", "2", "3"].includes(e.key)) return;

         const selectedValue = Number(e.key);

         const nextUnanswered = currentPageItems.find((item) => {
            const currentValue = values?.[currentSection.formId + item.id];
            return currentValue === undefined || currentValue === null || currentValue === "" || currentValue === 0 || currentValue === "0";
         });

         if (!nextUnanswered) return;

         e.preventDefault();
         void onChange(nextUnanswered.id, selectedValue);
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
   }, [sectionData?.isAdmin, sectionData?.onKeyDown, currentSection?.formId, sectionData?.page, currentPageItems, values, setValue]);

   function handleModalConfirm() {
      setShowValidationModal(false);
      if (sectionData.onNextSection) {
         sectionData.onNextSection(null, true);
      }
   }

   function handleModalCancel() {
      sectionData?.requestNextAsSection?.();
      sectionData?.setShowValidations?.(false);
      setShowValidationModal(false);
   }

   async function onChange(childId, value) {
      const id = currentSection.formId + childId;
      if (id in values && values[id] === value)
         setValue(childId, "");
      else
         setValue(childId, value);
   }

   return (
      <>
      <div className="sectionBody">
         <div className="instructions left">
            {parseSanitizedHtml(instructionsHtml)}
         </div>
         <div className="content center">
            {currentPageItems.map((item, i) => {
               return (
                  <Position
                     key={item.id || i}
                     item={item}
                     setValue={onChange}
                     values={values}
                     sectionData={sectionData}
                  />
               );
            })}
         </div>
      </div> 

      <ConfirmModal
         isOpen={showValidationModal}
         title="Just checking..."
         message="<p>We noticed the form has been left blank.</p><p>Was this by choice?</p>"
         onConfirm={handleModalConfirm}
         onCancel={handleModalCancel}
         confirmText="Yes, Continue"
         cancelText="No, Go Back"
         showCancelButton={true}
      />
      </>
   );
}


